import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { Star, User, Shield, Award, AlertTriangle, TrendingUp, TrendingDown, Hash, Plus, X, CheckCircle, XCircle } from "lucide-react";
import * as Yup from "yup";

import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { FormButton } from "../../../components/FormButton";
import { SelectSingle } from "../../../components/SelectSingle";
import { InputText } from "../../../components/InputText";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { api } from "../../../services/api";
import { useAuth } from "../../../contexts/AuthContext";
import { habboService } from "../../../services/habbo";

interface Oficial {
  id: string;
  nick: string;
}

interface Patente {
  id: string;
  patente: string;
  hierarquia: number;
}

interface Status {
  id: string;
  nome_status: string;
}

interface Sigla {
  id: string;
  sigla: string;
  descricao?: string;
}

interface NickEntry {
  id: number;
  nick: string;
  nickValid: boolean | null;
  nickValidating: boolean;
  error?: string;
}

interface SiglaNickEntry {
  id: number;
  nick: string;
  error?: string;
}

interface BatchResult {
  nick: string;
  success: boolean;
  message: string;
}

interface PromoverSharedData {
  id_militar_promoveu: string;
  id_patente: string;
  id_status: string;
}

let _nickEntryCounter = 0;
const newNickEntry = (): NickEntry => ({
  id: ++_nickEntryCounter,
  nick: "",
  nickValid: null,
  nickValidating: false,
});
const newSiglaNickEntry = (): SiglaNickEntry => ({
  id: ++_nickEntryCounter,
  nick: "",
});

const promoverSharedSchema = Yup.object().shape({
  id_militar_promoveu: Yup.string().required("Campo oficial responsável obrigatório"),
  id_patente: Yup.string().required("Campo patente obrigatório"),
  id_status: Yup.string().required("Campo status obrigatório"),
});

export function GestaoMilitares() {
  const { enqueueSnackbar } = useSnackbar();
  const { militar } = useAuth();

  const [activeTab, setActiveTab] = useState<'promover' | 'sigla'>('promover');

  // States para Promover/Rebaixar
  const [selectOficiais, setSelectOficiais] = useState<Oficial[]>([]);
  const [selectPatentes, setSelectPatentes] = useState<Patente[]>([]);
  const [selectStatus, setSelectStatus] = useState<Status[]>([]);
  const [promoverShared, setPromoverShared] = useState<PromoverSharedData>({
    id_militar_promoveu: militar?.id || "",
    id_patente: "",
    id_status: "",
  });
  const [promoverSharedErrors, setPromoverSharedErrors] = useState<Partial<PromoverSharedData>>({});
  const [nickEntries, setNickEntries] = useState<NickEntry[]>([newNickEntry()]);
  const [promoverLoading, setPromoverLoading] = useState(false);
  const [promoverResults, setPromoverResults] = useState<BatchResult[]>([]);
  const nickTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  // States para Alterar Sigla
  const [selectSiglas, setSelectSiglas] = useState<Sigla[]>([]);
  const [siglaId, setSiglaId] = useState("");
  const [siglaIdError, setSiglaIdError] = useState("");
  const [siglaNickEntries, setSiglaNickEntries] = useState<SiglaNickEntry[]>([newSiglaNickEntry()]);
  const [siglaLoading, setSiglaLoading] = useState(false);
  const [siglaResults, setSiglaResults] = useState<BatchResult[]>([]);

  useEffect(() => {
    api.get("/militares/listOficiais").then(response => {
      setSelectOficiais(response.data);
    });
    api.get("/patentes").then(response => {
      setSelectPatentes(response.data);
    });
    api.get("/status").then(response => {
      setSelectStatus(response.data);
      if (response.data.length > 0) {
        setPromoverShared(prev => ({ ...prev, id_status: response.data[0].id }));
      }
    });

    // Carregar dados para siglas
    api.get("/siglas").then(response => {
      setSelectSiglas(response.data);
    });
  }, []);

  // ── Handlers de nick - Promover ──────────────────────────────────────────
  const handleNickEntryChange = (id: number, value: string) => {
    const cleanValue = value.replace(/\s/g, "");
    setNickEntries(prev => prev.map(e =>
      e.id === id ? { ...e, nick: cleanValue, error: undefined, nickValid: cleanValue ? e.nickValid : null } : e
    ));

    const existing = nickTimeoutsRef.current.get(id);
    if (existing) clearTimeout(existing);

    if (cleanValue) {
      setNickEntries(prev => prev.map(e =>
        e.id === id ? { ...e, nickValidating: true, nickValid: null } : e
      ));
      const timeout = setTimeout(() => validateNickInHabbo(id, cleanValue), 800);
      nickTimeoutsRef.current.set(id, timeout);
    } else {
      setNickEntries(prev => prev.map(e =>
        e.id === id ? { ...e, nickValid: null, nickValidating: false } : e
      ));
    }
  };

  const validateNickInHabbo = async (id: number, nick: string) => {
    try {
      const response = await habboService.checkUserExists(nick);
      setNickEntries(prev => prev.map(e =>
        e.id === id
          ? {
              ...e,
              nickValid: response.exists,
              nickValidating: false,
              error: response.exists ? undefined : "Usuário não encontrado no Habbo",
            }
          : e
      ));
    } catch {
      setNickEntries(prev => prev.map(e =>
        e.id === id ? { ...e, nickValid: null, nickValidating: false } : e
      ));
    }
  };

  const addNickEntry = () => setNickEntries(prev => [...prev, newNickEntry()]);

  const removeNickEntry = (id: number) => {
    if (nickEntries.length === 1) return;
    setNickEntries(prev => prev.filter(e => e.id !== id));
    const timeout = nickTimeoutsRef.current.get(id);
    if (timeout) { clearTimeout(timeout); nickTimeoutsRef.current.delete(id); }
  };

  // ── Handlers de nick - Sigla ─────────────────────────────────────────────
  const handleSiglaNickChange = (id: number, value: string) => {
    const cleanValue = value.replace(/\s/g, "");
    setSiglaNickEntries(prev => prev.map(e =>
      e.id === id ? { ...e, nick: cleanValue, error: undefined } : e
    ));
  };

  const addSiglaNickEntry = () => setSiglaNickEntries(prev => [...prev, newSiglaNickEntry()]);

  const removeSiglaNickEntry = (id: number) => {
    if (siglaNickEntries.length === 1) return;
    setSiglaNickEntries(prev => prev.filter(e => e.id !== id));
  };

  // ── Submit Promover ──────────────────────────────────────────────────────
  const handlePromoverSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPromoverResults([]);

    let sharedValid = true;
    try {
      promoverSharedSchema.validateSync(promoverShared, { abortEarly: false });
      setPromoverSharedErrors({});
    } catch (error) {
      sharedValid = false;
      if (error instanceof Yup.ValidationError) {
        const errs: Partial<PromoverSharedData> = {};
        error.inner.forEach(err => {
          if (err.path) errs[err.path as keyof PromoverSharedData] = err.message;
        });
        setPromoverSharedErrors(errs);
      }
    }

    const filledEntries = nickEntries.filter(e => e.nick.trim());
    if (filledEntries.length === 0) {
      setNickEntries(prev => prev.map(e => ({ ...e, error: "Informe ao menos um nick" })));
      return;
    }

    let allNicksValid = true;
    setNickEntries(prev => prev.map(e => {
      if (!e.nick.trim()) return { ...e, error: "Nick obrigatório" };
      if (e.nickValid !== true) {
        allNicksValid = false;
        return { ...e, error: e.error || "Aguarde a validação do nick no Habbo" };
      }
      return e;
    }));

    if (!sharedValid || !allNicksValid) return;

    setPromoverLoading(true);
    const results: BatchResult[] = [];

    for (const entry of filledEntries) {
      try {
        await api.post("/militares", {
          nick: entry.nick,
          id_militar_promoveu: promoverShared.id_militar_promoveu,
          id_patente: promoverShared.id_patente,
          id_status: promoverShared.id_status,
        });
        results.push({ nick: entry.nick, success: true, message: "Alterado com sucesso" });
      } catch (error: any) {
        results.push({
          nick: entry.nick,
          success: false,
          message: error.response?.data?.message || "Erro ao processar",
        });
      }
    }

    setPromoverResults(results);
    setPromoverLoading(false);

    const successes = results.filter(r => r.success).length;
    const failures = results.filter(r => !r.success).length;

    if (failures === 0) {
      enqueueSnackbar(`${successes} militar(es) alterado(s) com sucesso!`, { variant: "success" });
      setNickEntries([newNickEntry()]);
      setPromoverShared({ id_militar_promoveu: militar?.id || "", id_patente: "", id_status: selectStatus[0]?.id || "" });
    } else if (successes > 0) {
      enqueueSnackbar(`${successes} sucesso(s) e ${failures} erro(s). Verifique o resumo abaixo.`, { variant: "warning" });
      setNickEntries(prev => {
        const failed = prev.filter(e => {
          const res = results.find(r => r.nick === e.nick);
          return !res?.success;
        });
        return failed.length > 0 ? failed : [newNickEntry()];
      });
    } else {
      enqueueSnackbar("Erro ao processar todos os militares. Verifique o resumo abaixo.", { variant: "error" });
    }
  };

  // ── Submit Sigla ─────────────────────────────────────────────────────────
  const handleSiglaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSiglaResults([]);

    if (!siglaId) {
      setSiglaIdError("Campo sigla obrigatório");
      return;
    }
    setSiglaIdError("");

    const filledEntries = siglaNickEntries.filter(e => e.nick.trim());
    if (filledEntries.length === 0) {
      setSiglaNickEntries(prev => prev.map(e => ({ ...e, error: "Informe ao menos um nick" })));
      return;
    }

    let hasEmpty = false;
    setSiglaNickEntries(prev => prev.map(e => {
      if (!e.nick.trim()) { hasEmpty = true; return { ...e, error: "Nick obrigatório" }; }
      return e;
    }));
    if (hasEmpty) return;

    setSiglaLoading(true);
    const isRemoving = siglaId === "remove";
    const results: BatchResult[] = [];

    for (const entry of filledEntries) {
      try {
        await api.patch("/militares/updateSigla", {
          nick: entry.nick,
          id_sigla: isRemoving ? null : siglaId,
        });
        results.push({ nick: entry.nick, success: true, message: isRemoving ? "Sigla removida" : "Sigla alterada" });
      } catch (error: any) {
        results.push({
          nick: entry.nick,
          success: false,
          message: error.response?.data?.message || "Erro ao processar",
        });
      }
    }

    setSiglaResults(results);
    setSiglaLoading(false);

    const successes = results.filter(r => r.success).length;
    const failures = results.filter(r => !r.success).length;

    if (failures === 0) {
      enqueueSnackbar(
        isRemoving ? `Sigla removida de ${successes} militar(es)!` : `Sigla alterada em ${successes} militar(es)!`,
        { variant: "success" }
      );
      setSiglaNickEntries([newSiglaNickEntry()]);
      setSiglaId("");
    } else if (successes > 0) {
      enqueueSnackbar(`${successes} sucesso(s) e ${failures} erro(s). Verifique o resumo abaixo.`, { variant: "warning" });
      setSiglaNickEntries(prev => {
        const failed = prev.filter(e => {
          const res = results.find(r => r.nick === e.nick);
          return !res?.success;
        });
        return failed.length > 0 ? failed : [newSiglaNickEntry()];
      });
    } else {
      enqueueSnackbar("Erro ao processar todos os militares. Verifique o resumo abaixo.", { variant: "error" });
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const getPatenteName = (patenteId: string) =>
    selectPatentes.find(p => p.id === patenteId)?.patente || "";

  const isPromotion = (patenteId: string) => {
    const patente = selectPatentes.find(p => p.id === patenteId);
    return patente && patente.hierarquia > 0;
  };

  const getSiglaInfo = (id: string) => selectSiglas.find(s => s.id === id);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Star className="h-6 w-6 text-primary" />
          Gestão de Militares
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie patentes, status e siglas dos militares da corporação
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-muted p-1 rounded-lg w-fit">
        <Button
          variant={activeTab === 'promover' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('promover')}
          className={activeTab === 'promover' ? 'bg-card shadow-sm' : ''}
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          Promover/Rebaixar
        </Button>
        <Button
          variant={activeTab === 'sigla' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('sigla')}
          className={activeTab === 'sigla' ? 'bg-card shadow-sm' : ''}
        >
          <Hash className="w-4 h-4 mr-2" />
          Alterar Sigla
        </Button>
      </div>

      {/* ── Tab Promover/Rebaixar ── */}
      {activeTab === 'promover' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[oklch(0.35_0.15_142)] flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Alterar Patente e Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePromoverSubmit} className="space-y-6">
                  {/* Campos compartilhados */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <SelectSingle
                        title="Oficial Responsável"
                        name="id_militar_promoveu"
                        value={promoverShared.id_militar_promoveu}
                        onChange={(e) => {
                          setPromoverShared(prev => ({ ...prev, id_militar_promoveu: e.target.value }));
                          setPromoverSharedErrors(prev => ({ ...prev, id_militar_promoveu: undefined }));
                        }}
                        error={promoverSharedErrors.id_militar_promoveu}
                        required
                      >
                        <option value="" disabled>Selecione o oficial responsável</option>
                        {selectOficiais.map(({ id, nick }) => (
                          <option key={id} value={id}>{nick}</option>
                        ))}
                      </SelectSingle>
                    </div>

                    <div>
                      <SelectSingle
                        title="Nova Patente"
                        name="id_patente"
                        value={promoverShared.id_patente}
                        onChange={(e) => {
                          setPromoverShared(prev => ({ ...prev, id_patente: e.target.value }));
                          setPromoverSharedErrors(prev => ({ ...prev, id_patente: undefined }));
                        }}
                        error={promoverSharedErrors.id_patente}
                        required
                      >
                        <option value="" disabled>Selecione a patente</option>
                        {selectPatentes.map(({ id, patente }) => (
                          <option key={id} value={id}>{patente}</option>
                        ))}
                      </SelectSingle>
                    </div>

                    <div>
                      <SelectSingle
                        title="Status"
                        name="id_status"
                        value={promoverShared.id_status}
                        onChange={(e) => {
                          setPromoverShared(prev => ({ ...prev, id_status: e.target.value }));
                          setPromoverSharedErrors(prev => ({ ...prev, id_status: undefined }));
                        }}
                        error={promoverSharedErrors.id_status}
                        required
                      >
                        {selectStatus.map(({ id, nome_status }) => (
                          <option key={id} value={id}>{nome_status}</option>
                        ))}
                      </SelectSingle>
                    </div>
                  </div>

                  {/* Lista de nicks */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Nicks dos Militares <span className="text-red-500">*</span>
                    </Label>
                    {nickEntries.map((entry, index) => (
                      <div key={entry.id} className="flex items-start gap-2">
                        <div className="flex-1">
                          <InputText
                            name={`nick-${entry.id}`}
                            title=""
                            placeholder={`Nick do militar ${index + 1}`}
                            value={entry.nick}
                            onChange={(e) => handleNickEntryChange(entry.id, e.target.value)}
                            error={entry.error}
                          />
                          {entry.nickValidating && (
                            <p className="text-xs text-blue-600 mt-1">Verificando no Habbo...</p>
                          )}
                          {!entry.nickValidating && entry.nickValid === true && (
                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" /> Usuário encontrado no Habbo
                            </p>
                          )}
                        </div>
                        {nickEntries.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="mt-1 h-8 w-8 p-0 text-destructive hover:text-destructive"
                            onClick={() => removeNickEntry(entry.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addNickEntry}
                      className="flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      Adicionar Nick
                    </Button>
                  </div>

                  {/* Resultado em lote */}
                  {promoverResults.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Resultado da operação:</Label>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {promoverResults.map((result, i) => (
                          <div key={i} className={`flex items-center gap-2 text-sm p-2 rounded ${result.success ? 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'}`}>
                            {result.success
                              ? <CheckCircle className="h-4 w-4 flex-shrink-0" />
                              : <XCircle className="h-4 w-4 flex-shrink-0" />}
                            <span className="font-medium">{result.nick}:</span>
                            <span>{result.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3">
                    <FormButton
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setNickEntries([newNickEntry()]);
                        setPromoverShared({ id_militar_promoveu: militar?.id || "", id_patente: "", id_status: selectStatus[0]?.id || "" });
                        setPromoverSharedErrors({});
                        setPromoverResults([]);
                      }}
                    >
                      Limpar
                    </FormButton>
                    <FormButton
                      type="submit"
                      loading={promoverLoading}
                      className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                    >
                      {promoverLoading ? "Processando..." : "Salvar Alterações"}
                    </FormButton>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Painel lateral - Promover */}
          <div className="space-y-4">
            {promoverShared.id_patente && (
              <Card className={`border-l-4 ${isPromotion(promoverShared.id_patente) ? 'border-l-green-500' : 'border-l-yellow-500'}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    {isPromotion(promoverShared.id_patente) ? (
                      <><TrendingUp className="h-4 w-4 text-green-600" /> Promoção</>
                    ) : (
                      <><TrendingDown className="h-4 w-4 text-yellow-600" /> Rebaixamento</>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Nova Patente:</span>
                    <Badge className={isPromotion(promoverShared.id_patente)
                      ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700"
                      : "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700"
                    }>
                      {getPatenteName(promoverShared.id_patente)}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-l-4 border-l-amber-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4" />
                  Avisos Importantes
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Shield className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Verifique se os militares possuem os requisitos para a nova patente.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Award className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Esta ação será registrada no histórico de cada militar.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Certifique-se de que os nicks estão corretos.</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ── Tab Alterar Sigla ── */}
      {activeTab === 'sigla' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[oklch(0.35_0.15_142)] flex items-center gap-2">
                  <Hash className="h-5 w-5" />
                  Alterar Sigla
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSiglaSubmit} className="space-y-6">
                  {/* Seletor de sigla */}
                  <div>
                    <Label>Nova Sigla <span className="text-red-500">*</span></Label>
                    <SelectSingle
                      title=""
                      name="id_sigla"
                      value={siglaId}
                      onChange={(e) => { setSiglaId(e.target.value); setSiglaIdError(""); }}
                      error={siglaIdError}
                      className="mt-1"
                    >
                      <option value="" disabled>Selecione a nova sigla</option>
                      <option value="remove">— Retirar sigla —</option>
                      {selectSiglas.map(({ id, sigla }) => (
                        <option key={id} value={id}>{sigla}</option>
                      ))}
                    </SelectSingle>
                  </div>

                  {/* Lista de nicks */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Nicks dos Militares <span className="text-red-500">*</span>
                    </Label>
                    {siglaNickEntries.map((entry, index) => (
                      <div key={entry.id} className="flex items-start gap-2">
                        <div className="flex-1">
                          <InputText
                            name={`sigla-nick-${entry.id}`}
                            title=""
                            placeholder={`Nick do militar ${index + 1}`}
                            value={entry.nick}
                            onChange={(e) => handleSiglaNickChange(entry.id, e.target.value)}
                            error={entry.error}
                          />
                        </div>
                        {siglaNickEntries.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="mt-1 h-8 w-8 p-0 text-destructive hover:text-destructive"
                            onClick={() => removeSiglaNickEntry(entry.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addSiglaNickEntry}
                      className="flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      Adicionar Nick
                    </Button>
                  </div>

                  {/* Resultado em lote */}
                  {siglaResults.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Resultado da operação:</Label>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {siglaResults.map((result, i) => (
                          <div key={i} className={`flex items-center gap-2 text-sm p-2 rounded ${result.success ? 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'}`}>
                            {result.success
                              ? <CheckCircle className="h-4 w-4 flex-shrink-0" />
                              : <XCircle className="h-4 w-4 flex-shrink-0" />}
                            <span className="font-medium">{result.nick}:</span>
                            <span>{result.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <FormButton
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSiglaNickEntries([newSiglaNickEntry()]);
                        setSiglaId("");
                        setSiglaIdError("");
                        setSiglaResults([]);
                      }}
                    >
                      Limpar
                    </FormButton>
                    <FormButton
                      type="submit"
                      loading={siglaLoading}
                      className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                    >
                      {siglaLoading
                        ? "Salvando..."
                        : siglaId === "remove"
                        ? "Retirar Sigla"
                        : "Salvar Alteração"}
                    </FormButton>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Painel lateral - Sigla */}
          <div className="space-y-6">
            {siglaNickEntries.some(e => e.nick) && siglaId && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
                    Preview da Alteração
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {siglaNickEntries.filter(e => e.nick).map(entry => (
                    <div key={entry.id} className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-950/40 rounded-lg border border-transparent dark:border-blue-800">
                      <div className="flex items-center gap-2">
                        <User className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                        <span className="text-xs font-medium text-blue-900 dark:text-blue-300">{entry.nick}</span>
                      </div>
                      {siglaId === "remove" ? (
                        <Badge variant="destructive" className="text-xs bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300">
                          Sem sigla
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200">
                          {getSiglaInfo(siglaId)?.sigla}
                        </Badge>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card className="border-l-4 border-l-amber-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4" />
                  Avisos Importantes
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Hash className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Certifique-se de que os nicks dos militares estão corretos.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Hash className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>As siglas serão alteradas imediatamente no sistema.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Award className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>As ações ficarão registradas no histórico.</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
